/** Generate Text — Canonical Generation Card
 *
 * Vertical order (HARD): Header → 生成结果 → 输入资产 → 生成参数 → 补充要求 → Footer
 * (Provider | Skill | 生成文本). Collapsed: Header + Result only.
 *
 * Visual language mirrors ScriptGenerationNode / GenerateImageNode (`pcv-gen-*`,
 * `pcv-brief-*`). Empty additionalInstruction does NOT block Generate.
 */

import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { Handle, Position, useConnection, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  TEXT_GENERATION_CAPABILITY,
  createTextGenerationRun,
  getTextGenerationRun,
  materializeImageGenerationOutput,
  type SaasApiError,
  type TextGenerationRunState,
} from '../../../../api'
import { countDisconnectedMentions, resolvePromptForSubmit, stripMentionsForEdge } from '../assetMention'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import {
  buildExplicitProviderSelectionPatch,
  findProviderOptionById,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { buildCapabilityProviderSelectOptions } from '../capabilityProviderSelectDisplay'
import {
  readProviderDisplayNameFromCache,
  writeProviderDisplayNamesBulk,
  writeProviderDisplayNameToCache,
} from '../providerOptionDisplayCache'
import {
  mediaPreviewHandleTopPx,
  MEDIA_CARD_HEADER_HEIGHT_PX,
  type PcvNodeShellCssVars,
} from '../canvasNodeHandleAnchor'
import {
  GENERIC_INPUT_HANDLE,
  computeMaterialAliases,
  getNodeIO,
  imageReferenceAssetHandleId,
  nodeOutputDataTypes,
  type DataType,
  type InputMaterial,
} from '../dataflow'
import { resolveGenerateImageInputHandleVisibility } from '../generateImageInputHandleVisibility'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import {
  buildTextGenerationSubmitPayload,
  computeGenerateTextInputFingerprint,
  isGenerateTextResultStale,
  primarySourceNodeTypeFromMaterials,
  pushGenerateTextHistory,
  resultFromTextGenerationRun,
  syncInputAssetsFromMaterials,
  buildGenerateTextNewRunPresentationPatch,
  isGenerateTextRunBusy,
  resolveGenerateTextCurrentPresentation,
  shouldApplyGenerateTextRunUpdate,
} from '../generateTextCanvas'
import { resolveGenerateTextImageInputs } from '../generateTextReadiness'
import {
  countGenerateTextReferenceImages,
  GENERATE_TEXT_MAX_REFERENCE_IMAGES,
} from '../generateTextImageInput'
import {
  GENERATE_TEXT_INSTRUCTION_MAX,
  normalizeGenerateTextFieldDefaults,
  truncateGraphemes,
  type GenerateTextContentLength,
  type GenerateTextCurrentResult,
  type GenerateTextHistoryEntry,
  type GenerateTextLanguage,
  type GenerateTextOutputFormat,
} from '../generateTextContract'
import { copyGenerateTextResultToClipboard, getCopyableGenerateTextResult } from '../generateTextCopy'
import { exportGeneratedText } from '../generateTextExport'
import {
  resolveGenerateTextTenantFailureI18nKey,
  shouldShowGenerateTextTenantFailureBanner,
} from '../generateTextTenantFailurePresentation'
import {
  resolveGenerateTextCardWidthCss,
  resolveGenerateTextInlineMinWidthPx,
} from '../generateTextLayout'
import { evaluateGenerateTextReadiness } from '../generateTextReadiness'
import { resolveGenerateTextSkill } from '../generateTextSkills'
import { primaryBlockingReason } from '../generationReadiness'
import { useNodeDefinition } from '../nodeRegistry'
import type { FlowNodeData } from '../types'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import { useCapabilityTextSkills } from '../useCapabilityTextSkills'
import {
  VIDEO_PROMPT_CONTEXT_IN_HANDLE,
  VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
  isVideoPromptEnhancementPurpose,
  looksLikePromptEnhancementRefusal,
} from '../videoPromptEnhancement'
import AssetMentionPromptEditor from './AssetMentionPromptEditor'
import InputMaterialsRow from './InputMaterialsRow'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import { useMaterialThumbMap } from './useMaterialThumbMap'

const POLL_INTERVAL_MS = 2500
const COPY_FEEDBACK_MS = 1800
const RESULT_PREVIEW_GRAPHEMES = 420
const GENERATE_TEXT_RESULT_HEIGHT_COLLAPSED = 160
const GENERATE_TEXT_RESULT_HEIGHT_EXPANDED = 220

function isRunNotFound(err: unknown): boolean {
  const apiErr = err as SaasApiError
  return apiErr?.status === 404 || apiErr?.code === 'not_found'
}

function useImageAssetThumb(
  workspaceId: string,
  asset: { entityId?: string | null; generatedOutputId?: string | null; nodeType?: string | null },
): string | null {
  return useMemo(
    () =>
      resolveCanvasImageThumbUrl(workspaceId, {
        entityId: asset.entityId,
        generatedOutputId: asset.generatedOutputId,
        nodeType: asset.nodeType,
      }),
    [workspaceId, asset.entityId, asset.generatedOutputId, asset.nodeType],
  )
}

function TextResultBody({
  result,
  showExpanded,
}: {
  result: GenerateTextCurrentResult
  showExpanded: boolean
}) {
  const text = result.text || ''
  const format = String(result.format || 'plain').toLowerCase()
  const display = showExpanded ? text : truncateGraphemes(text, RESULT_PREVIEW_GRAPHEMES)
  if (format === 'markdown') {
    return (
      <div className="pcv-gen-text-result pcv-gen-text-result--markdown pcv-canvas-result-plaintext nodrag" data-testid="generate-text-result-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{display}</ReactMarkdown>
      </div>
    )
  }
  return (
    <pre className="pcv-gen-text-result pcv-gen-text-result--plain pcv-canvas-result-plaintext nodrag" data-testid="generate-text-result-body">
      {display}
    </pre>
  )
}

function HistoryList({ entries }: { entries: GenerateTextHistoryEntry[] }) {
  const { t } = useTranslation('canvas')
  if (!entries.length) {
    return <div className="pcv-gen-history-empty">{t('generateText.historyEmpty')}</div>
  }
  return (
    <div className="pcv-gen-history-list">
      {entries.map((h, idx) => (
        <div key={`${h.version}-${idx}`} className="pcv-gen-history-item">
          <span className="pcv-gen-history-version">V{h.version}</span>
          <div className="pcv-gen-history-body">
            <div className="pcv-gen-history-title">{truncateGraphemes(h.result.text || '', 80)}</div>
            <div className="pcv-gen-history-meta">
              {h.result.language || ''} · {h.result.format || 'plain'}
              {h.completedAt ? ` · ${h.completedAt}` : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function GenerateTextNodeImpl({ id, data, selected }: NodeProps) {
  const { t, i18n } = useTranslation('canvas')
  const { mediaHeader } = useCanvasGenerationI18n()
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string; projectId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const workspaceId = nodeData.workspaceId || ''
  const projectId = nodeData.projectId || ''
  const fields = nodeData.fields || {}
  const inputMaterials = (nodeData.inputMaterials || []) as InputMaterial[]
  const imageMaterials = useMemo(() => inputMaterials.filter((m) => m.kind === 'IMAGE'), [inputMaterials])
  const textMaterials = useMemo(
    () => inputMaterials.filter((m): m is Extract<InputMaterial, { kind: 'TEXT' }> => m.kind === 'TEXT'),
    [inputMaterials],
  )
  const contextMaterials = useMemo(
    () => inputMaterials.filter((m) => m.kind === 'IMAGE' || m.kind === 'TEXT'),
    [inputMaterials],
  )
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const fieldDefaults = normalizeGenerateTextFieldDefaults(fields, i18n.language)
  const language = fieldDefaults.language
  const contentLength = fieldDefaults.contentLength
  const outputFormat = fieldDefaults.outputFormat
  const additionalInstruction = fieldDefaults.additionalInstruction
  const aliasByEdgeId = useMemo(() => computeMaterialAliases(inputMaterials, t), [inputMaterials, t])
  const thumbUrlByEdgeId = useMaterialThumbMap(workspaceId, inputMaterials)
  const disconnectedMentionCount = useMemo(
    () => countDisconnectedMentions(additionalInstruction, aliasByEdgeId),
    [additionalInstruction, aliasByEdgeId],
  )
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null) ?? null
  const nodePersistedProviderDisplayName =
    typeof fields.providerDisplayName === 'string' ? fields.providerDisplayName : null
  const currentResult = (fields.currentResult as GenerateTextCurrentResult | null) ?? null
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as GenerateTextHistoryEntry[])
    : []
  const runStatus = (fields.runStatus as string | null) ?? null
  const activeRunId = (fields.activeRunId as string | null) ?? null
  const isPromptEnhancement = isVideoPromptEnhancementPurpose(fields.purpose)
  const boundVideoNodeId =
    typeof fields.boundVideoNodeId === 'string' && fields.boundVideoNodeId.trim()
      ? fields.boundVideoNodeId.trim()
      : null
  const boundOriginalPrompt = useMemo(() => {
    if (!isPromptEnhancement) return ''
    const snap = fields.videoPromptContextSnapshot
    if (snap && typeof snap === 'object') {
      return String((snap as { original_prompt?: string }).original_prompt || '').trim()
    }
    return ''
  }, [fields.videoPromptContextSnapshot, isPromptEnhancement])

  const [historyOpen, setHistoryOpen] = useState(false)
  const [chipBatch, setChipBatch] = useState(0)
  const [sectionCopyFeedback, setSectionCopyFeedback] = useState(false)
  const sectionCopyFeedbackTimerRef = useRef<number | null>(null)
  const pollTimerRef = useRef<number | null>(null)
  const submitLockRef = useRef(false)
  const activeRunIdRef = useRef<string | null>(activeRunId)
  const nodeShellRef = useRef<HTMLDivElement | null>(null)
  const promptContextSectionRef = useRef<HTMLDivElement | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()

  // Heal missing defaults into real string state (never leave null language).
  useEffect(() => {
    const patch: Record<string, unknown> = {}
    if (!(typeof fields.language === 'string' && fields.language.trim())) patch.language = fieldDefaults.language
    if (!(typeof fields.contentLength === 'string' && fields.contentLength.trim())) {
      patch.contentLength = fieldDefaults.contentLength
    }
    if (!(typeof fields.outputFormat === 'string' && fields.outputFormat.trim())) {
      patch.outputFormat = fieldDefaults.outputFormat
    }
    if (typeof fields.additionalInstruction !== 'string') patch.additionalInstruction = ''
    if (Object.keys(patch).length === 0) return
    const next = patchCanvasFieldsIfChanged(fields, patch)
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- heal once when missing
  }, [fieldDefaults.language, fieldDefaults.contentLength, fieldDefaults.outputFormat, fields.language, fields.contentLength, fields.outputFormat, fields.additionalInstruction, id])

  const resultAreaHeight = showExpanded ? GENERATE_TEXT_RESULT_HEIGHT_EXPANDED : GENERATE_TEXT_RESULT_HEIGHT_COLLAPSED
  const handleTop = mediaPreviewHandleTopPx(resultAreaHeight, MEDIA_CARD_HEADER_HEIGHT_PX)
  const inputHandleVisibility = useMemo(
    () => resolveGenerateImageInputHandleVisibility(showExpanded, true),
    [showExpanded],
  )
  const stackedPerEdgeTargetHandleIds = useMemo(
    () => imageMaterials.map((m) => imageReferenceAssetHandleId(m.edgeId)),
    [imageMaterials],
  )
  const imageMaterialEdgeIdsKey = useMemo(() => imageMaterials.map((m) => m.edgeId).join('|'), [imageMaterials])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [
    id,
    imageMaterialEdgeIdsKey,
    resultAreaHeight,
    showExpanded,
    inputHandleVisibility.showExpandedSlotStackedPerEdgeHandles,
    stackedPerEdgeTargetHandleIds.join('|'),
    isPromptEnhancement,
    updateNodeInternals,
  ])

  useEffect(
    () => () => {
      if (sectionCopyFeedbackTimerRef.current != null) {
        window.clearTimeout(sectionCopyFeedbackTimerRef.current)
      }
    },
    [],
  )

  // Sync edge materials → inputAssets fingerprint field (no per-keystroke network).
  // VistoeraTASK-00583 — include TEXT materials (text_asset / generate_text sources),
  // not only IMAGE; otherwise a real edge still leaves the input-asset tray empty.
  useEffect(() => {
    const synced = syncInputAssetsFromMaterials(contextMaterials)
    const prev = Array.isArray(fields.inputAssets) ? fields.inputAssets : []
    const prevKey = JSON.stringify(prev)
    const nextKey = JSON.stringify(synced)
    if (prevKey === nextKey) return
    const fingerprint = computeGenerateTextInputFingerprint({ fields: { ...fields, inputAssets: synced }, inputMaterials })
    const hadResult = Boolean(currentResult && String(currentResult.text || '').length)
    const prevFp = typeof fields.generatedFromFingerprint === 'string' ? fields.generatedFromFingerprint : null
    const patch: Record<string, unknown> = { inputAssets: synced }
    if (hadResult && prevFp && prevFp !== fingerprint) {
      patch.needsRegeneration = true
    }
    const next = patchCanvasFieldsIfChanged(fields, patch)
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMaterials, id])

  const { options: providerOptions, error: providerOptionsError, loading: providerOptionsLoading } =
    useCapabilityProviderOptions(workspaceId, TEXT_GENERATION_CAPABILITY, 'generate_text')

  const {
    skills,
    defaultSkill,
    error: skillsError,
    loading: skillsLoading,
  } = useCapabilityTextSkills(workspaceId, TEXT_GENERATION_CAPABILITY)

  const sourceNodeType =
    primarySourceNodeTypeFromMaterials(imageMaterials) ||
    (typeof fields.preferredSkillKey === 'string' && fields.preferredSkillKey === 'outfit_interpretation'
      ? 'generate_tryon'
      : null)

  const imageInputCount = useMemo(
    () =>
      resolveGenerateTextImageInputs({ fields, inputMaterials: imageMaterials }).filter(
        (img) => img.assetId || img.generatedOutputId,
      ).length,
    [fields, imageMaterials],
  )

  const resolvedSkill = useMemo(
    () =>
      resolveGenerateTextSkill({
        skillSelectionSource,
        skillKey: (fields.skillKey as string | null) ?? null,
        skills,
        sourceNodeType,
        preferredSkillKey: (fields.preferredSkillKey as string | null) ?? null,
        imageInputCount,
      }),
    [fields.preferredSkillKey, fields.skillKey, imageInputCount, skillSelectionSource, skills, sourceNodeType],
  )

  const resolvedProviderOptionId = useMemo(
    () =>
      resolveCapabilityProviderOptionId({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        storedProviderDisplayName:
          typeof fields.providerDisplayName === 'string' ? fields.providerDisplayName : null,
        options: providerOptions,
      }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )

  const providerSelectView = useMemo(
    () =>
      buildCapabilityProviderSelectOptions({
        catalogOptions: providerOptions,
        resolvedProviderOptionId,
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        nodePersistedDisplayName: nodePersistedProviderDisplayName,
        workspaceCachedDisplayName: readProviderDisplayNameFromCache(workspaceId, resolvedProviderOptionId),
        catalogLoading: providerOptionsLoading,
        catalogError: providerOptionsError,
        loadingLabel: t('generateText.providerLoading'),
        unavailableSuffix: t('generation.engineUnavailableSuffix'),
        loadFailedPlaceholder: t('generateText.providerLoadFailed'),
      }),
    [
      nodePersistedProviderDisplayName,
      providerOptions,
      providerOptionsError,
      providerOptionsLoading,
      providerSelectionMode,
      resolvedProviderOptionId,
      storedProviderOptionId,
      t,
      workspaceId,
    ],
  )

  // Canonical revalidation: refresh workspace cache + node display metadata (UI only).
  useEffect(() => {
    if (!workspaceId || !providerOptions.length) return
    writeProviderDisplayNamesBulk(
      workspaceId,
      providerOptions.map((o) => ({
        providerOptionId: o.provider_option_id,
        displayName: o.frontend_display_name,
      })),
    )
    if (providerSelectionMode !== 'EXPLICIT' || !storedProviderOptionId) return
    const hit = findProviderOptionById(providerOptions, storedProviderOptionId)
    if (!hit?.frontend_display_name?.trim()) return
    const canonical = hit.frontend_display_name.trim()
    const persisted =
      typeof fields.providerDisplayName === 'string' ? fields.providerDisplayName.trim() : ''
    if (persisted === canonical) return
    const next = patchCanvasFieldsIfChanged(fields, { providerDisplayName: canonical })
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, providerOptions, providerSelectionMode, storedProviderOptionId, id])

  // Rebind EXPLICIT provider_option_id when canvas was authored on another environment.
  useEffect(() => {
    if (providerSelectionMode !== 'EXPLICIT' || !storedProviderOptionId || !resolvedProviderOptionId) return
    if (storedProviderOptionId === resolvedProviderOptionId) return
    if (findProviderOptionById(providerOptions, storedProviderOptionId)) return
    const rebound = findProviderOptionById(providerOptions, resolvedProviderOptionId)
    if (!rebound) return
    const next = patchCanvasFieldsIfChanged(fields, {
      providerOptionId: resolvedProviderOptionId,
      providerDisplayName: rebound.frontend_display_name?.trim() || fields.providerDisplayName,
    })
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    id,
    providerOptions,
    providerSelectionMode,
    resolvedProviderOptionId,
    storedProviderOptionId,
  ])

  const skillSelectOptions = useMemo(() => {
    const groups = new Map<string, Array<{ value: string; label: string; title?: string }>>()
    for (const skill of skills) {
      const group = skill.ownership_label || t('generateText.skillGroupOfficial')
      const list = groups.get(group) || []
      const versionLabel = skill.skill_version ? ` · ${skill.skill_version}` : ''
      list.push({
        value: skill.skill_key,
        label: `${skill.display_name || skill.skill_key}${skill.is_default ? ` (${t('generateText.skillDefaultBadge')})` : ''}`,
        title: `${skill.description || skill.display_name || ''}${versionLabel}`.trim(),
      })
      groups.set(group, list)
    }
    const opts: Array<{
      label?: string
      options?: Array<{ value: string; label: string; title?: string; disabled?: boolean }>
      value?: string
      title?: string
      disabled?: boolean
    }> = [
      {
        value: 'AUTO',
        label: defaultSkill
          ? `${t('generateText.skillAuto')} · ${defaultSkill.display_name}`
          : t('generateText.skillAuto'),
        title: defaultSkill?.description || undefined,
      },
    ]
    for (const [groupLabel, options] of groups) {
      opts.push({ label: groupLabel, options })
    }
    const explicitKey =
      skillSelectionSource === 'EXPLICIT' && typeof fields.skillKey === 'string' ? String(fields.skillKey).trim() : ''
    if (explicitKey && !skills.some((s) => s.skill_key === explicitKey)) {
      opts.push({
        value: explicitKey,
        label: t('generateText.skillUnavailableOption', { name: explicitKey }),
        disabled: true,
      })
    }
    return opts
  }, [defaultSkill, fields.skillKey, skillSelectionSource, skills, t])

  const skillSelectValue =
    skillSelectionSource === 'EXPLICIT' && fields.skillKey ? String(fields.skillKey) : 'AUTO'

  const isBusy = isGenerateTextRunBusy(runStatus)
  const presentedResult = useMemo(
    () => resolveGenerateTextCurrentPresentation({ currentResult, runStatus }),
    [currentResult, runStatus],
  )
  const needsRegeneration =
    !isBusy && runStatus !== 'ERROR' && isGenerateTextResultStale({ fields, inputMaterials })
  const hasResult = Boolean(presentedResult && String(presentedResult.text || '').length)

  const readiness = useMemo(
    () =>
      evaluateGenerateTextReadiness({
        fields,
        inputMaterials,
        providerOptions,
        providerLoading: providerOptionsLoading,
        providerError: providerOptionsError,
        skills,
        skillsLoading,
        skillsError,
        isBusy,
        sourceNodeType,
      }),
    [
      fields,
      inputMaterials,
      providerOptions,
      providerOptionsLoading,
      providerOptionsError,
      skills,
      skillsLoading,
      skillsError,
      isBusy,
      sourceNodeType,
    ],
  )
  const primaryBlock = useMemo(() => primaryBlockingReason(readiness), [readiness])
  const blockingHint = primaryBlock ? t(primaryBlock.messageKey) : null
  const canGenerate = readiness.ready

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const markParamsDirty = useCallback(
    (patch: Record<string, unknown>) => {
      if (hasResult) {
        patchFields({ ...patch, needsRegeneration: true })
      } else {
        patchFields(patch)
      }
    },
    [hasResult, patchFields],
  )

  const handleAdditionalInstructionChange = useCallback(
    (next: string) => {
      markParamsDirty({ additionalInstruction: next.slice(0, GENERATE_TEXT_INSTRUCTION_MAX) })
    },
    [markParamsDirty],
  )

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    activeRunIdRef.current = activeRunId
  }, [activeRunId])

  const applyRunState = useCallback(
    (run: TextGenerationRunState) => {
      if (!shouldApplyGenerateTextRunUpdate(run.run_id, activeRunIdRef.current)) {
        return
      }
      activeRunIdRef.current = run.run_id

      if (run.status === 'SUCCESS') {
        const fingerprint = computeGenerateTextInputFingerprint({ fields, inputMaterials })
        const result = resultFromTextGenerationRun(run, fingerprint)
        const history = pushGenerateTextHistory(currentResult, generationHistory)
        patchFields({
          runStatus: 'SUCCESS',
          activeRunId: run.run_id,
          currentResult: result,
          generationHistory: history,
          errorMessage: null,
          errorCode: null,
          needsRegeneration: false,
          generatedFromFingerprint: fingerprint,
        })
        nodeData.onChangeLatestRunId?.(id, run.run_id)
        if (isPromptEnhancement && boundVideoNodeId && result) {
          const text = result.text || ''
          if (!looksLikePromptEnhancementRefusal(text)) {
            nodeData.onVideoPromptEnhancementResult?.(boundVideoNodeId, {
              text,
              runId: run.run_id,
              skillKey: run.skill_key || (fields.skillKey as string | null) || VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY,
              skillVersion: run.skill_version || (fields.skillVersion as string | null) || 'v1',
              providerOptionId: (fields.providerOptionId as string | null) ?? null,
            })
          }
        }
        submitLockRef.current = false
        stopPolling()
        return
      }
      if (run.status === 'ERROR') {
        patchFields({
          runStatus: 'ERROR',
          activeRunId: run.run_id,
          currentResult: null,
          errorMessage:
            typeof run.error_message === 'string' && run.error_message.trim() ? run.error_message : null,
          errorCode: run.error_code,
          needsRegeneration: false,
        })
        submitLockRef.current = false
        stopPolling()
        return
      }
      patchFields({
        runStatus: run.status,
        activeRunId: run.run_id,
        errorMessage: null,
        errorCode: null,
      })
    },
    [
      boundVideoNodeId,
      currentResult,
      fields,
      generationHistory,
      id,
      inputMaterials,
      isPromptEnhancement,
      nodeData,
      patchFields,
      stopPolling,
      t,
    ],
  )

  const pollRun = useCallback(
    (runId: string) => {
      stopPolling()
      if (!workspaceId || !runId) return
      const tick = () => {
        getTextGenerationRun(workspaceId, runId)
          .then((run) => {
            applyRunState(run)
            if (isGenerateTextRunBusy(run.status)) {
              pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS)
            }
          })
          .catch((err) => {
            if (isRunNotFound(err)) {
              patchFields({
                runStatus: 'ERROR',
                errorMessage: 'generation_run_not_found',
                errorCode: 'not_found',
                activeRunId: null,
              })
              submitLockRef.current = false
              stopPolling()
              return
            }
            pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS)
          })
      }
      pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS)
    },
    [applyRunState, patchFields, stopPolling, t, workspaceId],
  )

  // Resume polling after reload when run still in flight.
  useEffect(() => {
    if (!workspaceId || !activeRunId) return
    if (!isGenerateTextRunBusy(runStatus)) return
    pollRun(activeRunId)
    return () => stopPolling()
  }, [activeRunId, pollRun, runStatus, stopPolling, workspaceId])

  // VistoeraTASK-00662 — ERROR nodes may recover after SaaS re-applies a
  // persisted Worker Job.result (results[].text). Re-fetch once on mount.
  useEffect(() => {
    if (!workspaceId || !activeRunId) return
    if (String(runStatus || '').toUpperCase() !== 'ERROR') return
    let cancelled = false
    getTextGenerationRun(workspaceId, activeRunId)
      .then((run) => {
        if (!cancelled) applyRunState(run)
      })
      .catch(() => {
        /* keep local ERROR; user can regenerate */
      })
    return () => {
      cancelled = true
    }
    // Intentionally mount/activeRunId scoped — avoid loops on ERROR→ERROR.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot recover
  }, [workspaceId, activeRunId])

  useEffect(() => () => stopPolling(), [stopPolling])

  const connection = useConnection()
  const draggedSourceType: DataType | undefined = useMemo(() => {
    if (!connection.inProgress || connection.fromHandle?.type !== 'source') return undefined
    const sourceNodeTypeLive = (connection.fromNode?.data as unknown as FlowNodeData | undefined)?.nodeType
    if (!sourceNodeTypeLive) return undefined
    return nodeOutputDataTypes(sourceNodeTypeLive)[0]
  }, [connection])
  const acceptedTypes = getNodeIO('generate_text')?.acceptedInputTypes || []
  const mediaDropState =
    draggedSourceType == null ? 'none' : acceptedTypes.includes(draggedSourceType) ? 'candidate' : 'incompatible'

  const handleGenerate = async () => {
    if (!canGenerate || submitLockRef.current || !workspaceId) return
    if (!resolvedSkill) return
    if (disconnectedMentionCount > 0) {
      message.error(t('generation.errorDisconnectedMentions'))
      return
    }
    const { resolved: resolvedAdditionalInstruction, hasDisconnectedMention } = resolvePromptForSubmit(
      additionalInstruction,
      aliasByEdgeId,
      t,
    )
    if (hasDisconnectedMention) {
      message.error(t('generation.errorDisconnectedMentions'))
      return
    }
    submitLockRef.current = true
    stopPolling()
    flushSync(() => {
      const presentation = buildGenerateTextNewRunPresentationPatch({
        previousResult: currentResult,
        generationHistory,
      })
      activeRunIdRef.current = null
      patchFields({
        ...presentation,
        runStatus: 'SUBMITTING',
        activeRunId: null,
      })
    })
    try {
      // Backend requires site asset_id — materialize generation outputs when needed.
      let materialsForSubmit = inputMaterials
      const images = resolveGenerateTextImageInputs({ fields, inputMaterials })
      const needsMaterialize = images.some((img) => !img.assetId && img.generatedOutputId)
      if (needsMaterialize) {
        const patchedMaterials = [...inputMaterials]
        for (let i = 0; i < patchedMaterials.length; i++) {
          const m = patchedMaterials[i]
          if (m.kind !== 'IMAGE') continue
          if (m.entityId || !m.generatedOutputId) continue
          const res = await materializeImageGenerationOutput(workspaceId, m.generatedOutputId)
          const siteAssetId = res?.data?.asset_id || null
          if (!siteAssetId) {
            throw Object.assign(new Error(t('generateText.readiness.inputAssetNotReady')), {
              code: 'INPUT_ASSET_NOT_READY',
            })
          }
          patchedMaterials[i] = { ...m, entityId: siteAssetId, ready: true }
        }
        materialsForSubmit = patchedMaterials
        const synced = syncInputAssetsFromMaterials(materialsForSubmit)
        patchFields({ inputAssets: synced })
      }

      const skillSource = skillSelectionSource === 'EXPLICIT' ? 'EXPLICIT' : 'AUTO'
      // Always refresh from live Video.promptText at submit — never rely on a stale snapshot alone.
      const liveSnapshot =
        isPromptEnhancement && nodeData.onResolveVideoPromptEnhancementContext
          ? nodeData.onResolveVideoPromptEnhancementContext(id)
          : null
      const scopedContext: Record<string, unknown> | null = liveSnapshot
        ? ({ ...liveSnapshot } as Record<string, unknown>)
        : isPromptEnhancement &&
            fields.videoPromptContextSnapshot &&
            typeof fields.videoPromptContextSnapshot === 'object'
          ? (fields.videoPromptContextSnapshot as Record<string, unknown>)
          : null
      if (liveSnapshot) {
        patchFields({ videoPromptContextSnapshot: liveSnapshot })
      }
      const payload = buildTextGenerationSubmitPayload({
        canvasNodeId: id,
        projectId: projectId || 'canvas',
        fields: {
          ...fields,
          additionalInstruction: resolvedAdditionalInstruction,
          inputAssets: syncInputAssetsFromMaterials(materialsForSubmit),
          ...(liveSnapshot ? { videoPromptContextSnapshot: liveSnapshot } : {}),
        },
        inputMaterials: materialsForSubmit,
        skillKey: resolvedSkill.skill_key,
        skillVersion: resolvedSkill.skill_version || fieldDefaults.skillVersion,
        skillSelectionSource: skillSource,
        providerSelectionMode,
        providerOptionId: providerSelectionMode === 'EXPLICIT' ? storedProviderOptionId : null,
        locale: i18n.language,
        scopedContext,
      })
      // AUTO path: pass preferred skill_key hint so backend honors try-on vs general.
      if (skillSource === 'AUTO' && resolvedSkill.skill_key) {
        payload.skill_key = resolvedSkill.skill_key
        payload.skill_version = resolvedSkill.skill_version
      }
      const run = await createTextGenerationRun(workspaceId, payload)
      activeRunIdRef.current = run.run_id
      applyRunState(run)
      submitLockRef.current = false
      if (isGenerateTextRunBusy(run.status)) {
        pollRun(run.run_id)
      }
    } catch (err) {
      const apiErr = err as SaasApiError
      const missingFields = (apiErr as { missing_fields?: string[] }).missing_fields
      const missing: string[] = Array.isArray(missingFields) ? missingFields : []
      const messageFromApi = apiErr?.message?.trim()
      const friendlyMessage =
        apiErr?.code === 'GENERATION_INPUT_REQUIRED' && missing.includes('input_assets')
          ? t('generateText.readiness.imageSkillRequiresAsset')
          : messageFromApi || t('generateText.submitFailed')
      patchFields({
        runStatus: 'ERROR',
        errorMessage: friendlyMessage,
        errorCode: apiErr?.code || null,
      })
      submitLockRef.current = false
    }
  }

  const copyDisplayedResult = useCallback(
    async (options?: { sectionFeedback?: boolean }) => {
      const text = getCopyableGenerateTextResult(presentedResult)
      if (!text) return false
      try {
        await copyGenerateTextResultToClipboard(text)
        if (options?.sectionFeedback) {
          setSectionCopyFeedback(true)
          if (sectionCopyFeedbackTimerRef.current != null) {
            window.clearTimeout(sectionCopyFeedbackTimerRef.current)
          }
          sectionCopyFeedbackTimerRef.current = window.setTimeout(() => {
            setSectionCopyFeedback(false)
            sectionCopyFeedbackTimerRef.current = null
          }, COPY_FEEDBACK_MS)
        } else {
          message.success(t('generateText.copied'))
        }
        return true
      } catch {
        message.error(t('generateText.copyFailed'))
        return false
      }
    },
    [presentedResult, t],
  )

  const handleDuplicateNode = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }

  const handleCopyResultSection = async (e: ReactMouseEvent) => {
    e.stopPropagation()
    await copyDisplayedResult({ sectionFeedback: true })
  }

  const handleDownloadResult = (e: ReactMouseEvent) => {
    e.stopPropagation()
    const text = presentedResult?.text
    if (!text) return
    try {
      exportGeneratedText({
        text,
        format: 'markdown',
        title: presentedResult?.title || t('nodes.generate_text.label', { defaultValue: def.label }),
      })
      message.success(t('generateText.downloaded'))
    } catch {
      message.error(t('generateText.downloadFailed'))
    }
  }

  const handleDelete = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (isBusy) return
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const openReplaceAssetPicker = () => {
    if (referenceImageCount >= GENERATE_TEXT_MAX_REFERENCE_IMAGES) {
      message.warning(t('generateText.readiness.tooManyReferenceImages'))
      return
    }
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: t('generateText.replaceAsset'),
      selectionMode: 'single',
      maxSelection: 1,
      onConfirm: (selections) => {
        const sel = selections?.[0]
        if (!sel) return
        const nextAssets = [
          {
            assetId: sel.assetId,
            assetType: 'image',
            sourceNodeId: null,
            displayName: sel.filename || sel.assetId,
            role: 'primary',
            generatedOutputId: null,
          },
        ]
        markParamsDirty({ inputAssets: nextAssets })
      },
    })
  }

  const suggestedChipsAll = Array.isArray(resolvedSkill?.suggested_instructions)
    ? resolvedSkill!.suggested_instructions!.filter((s) => typeof s === 'string' && s.trim())
    : []
  const CHIP_PAGE = 4
  const suggestedChips =
    suggestedChipsAll.length <= CHIP_PAGE
      ? suggestedChipsAll
      : Array.from({ length: CHIP_PAGE }, (_, i) => suggestedChipsAll[(chipBatch * CHIP_PAGE + i) % suggestedChipsAll.length])

  const resultStatusBadge = isBusy
    ? t('generateText.statusGenerating')
    : runStatus === 'ERROR'
      ? t('generateText.statusFailed')
      : hasResult
        ? t('generateText.statusReady')
        : t('generateText.statusEmpty')

  const languageOptions: Array<{ value: GenerateTextLanguage; label: string }> = [
    { value: 'auto', label: t('generateText.languageAuto') },
    { value: 'zh-CN', label: t('generateText.languageZhCN') },
    { value: 'en-US', label: t('generateText.languageEnUS') },
  ]
  // VistoeraTASK-00594 — free length first (mirrors Generate Image「自由比例」).
  const lengthOptions: Array<{ value: GenerateTextContentLength; label: string }> = [
    { value: 'AUTO', label: t('generateText.lengthFree') },
    { value: 'SHORT', label: t('generateText.lengthShort') },
    { value: 'MEDIUM', label: t('generateText.lengthMedium') },
    { value: 'LONG', label: t('generateText.lengthLong') },
  ]
  const formatOptions: Array<{ value: GenerateTextOutputFormat; label: string }> = [
    { value: 'auto', label: t('generateText.formatAuto') },
    { value: 'plain', label: t('generateText.formatPlain') },
    { value: 'markdown', label: t('generateText.formatMarkdown') },
  ]

  const referenceImageCount = countGenerateTextReferenceImages({ fields, inputMaterials })
  const assetCount = referenceImageCount + textMaterials.length
  const cardWidth = resolveGenerateTextCardWidthCss(showExpanded)
  const inlineMinWidth = resolveGenerateTextInlineMinWidthPx(showExpanded)
  const copyableResultText = getCopyableGenerateTextResult(presentedResult)

  const nodeShellStyle: PcvNodeShellCssVars = {
    ...(inlineMinWidth != null ? { width: cardWidth, minWidth: inlineMinWidth } : { width: cardWidth }),
    '--pcv-rail-top': `${handleTop}px`,
  }

  return (
    <div
      ref={nodeShellRef}
      className={`pcv-node pcv-node--generate-text${selected ? ' is-selected' : ''}${mediaDropState === 'candidate' ? ' is-drop-accept' : ''}${mediaDropState === 'incompatible' ? ' is-drop-reject' : ''} ${canvasNodeShellStateClass(showExpanded)}`}
      style={nodeShellStyle}
      data-testid="canvas-generate-text-node"
      data-canonical-order="header,result,input-assets,params,instruction,footer"
      data-expanded={showExpanded ? 'yes' : 'no'}
    >
      {inputHandleVisibility.showCollapsedStandardInputHandle ? (
        <Handle
          type="target"
          position={Position.Left}
          id={GENERIC_INPUT_HANDLE}
          className="pcv-handle pcv-handle--lg"
          style={{ top: handleTop }}
          data-testid="generate-text-collapsed-aggregate-in-handle"
        />
      ) : null}
      {inputHandleVisibility.showLegacyHiddenReferenceAnchors
        ? imageMaterials.map((m) => (
            <Handle
              key={`collapsed-ref::${m.edgeId}`}
              type="target"
              position={Position.Left}
              id={imageReferenceAssetHandleId(m.edgeId)}
              className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden"
              style={{ top: handleTop }}
            />
          ))
        : null}
      {isPromptEnhancement && !showExpanded ? (
        <Handle
          type="target"
          position={Position.Left}
          id={VIDEO_PROMPT_CONTEXT_IN_HANDLE}
          className="pcv-handle pcv-handle--prompt-context"
          style={{ top: handleTop, visibility: 'hidden', pointerEvents: 'none' }}
          aria-hidden
          data-testid="generate-text-prompt-context-in-handle"
        />
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        id="text-out"
        className="pcv-handle pcv-handle--lg"
        style={{ top: handleTop }}
        data-testid="generate-text-text-out-handle"
      />

      {/* ① Header — icon + title only; keep existing right actions */}
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={t('nodes.generate_text.label', { defaultValue: def.label })}
        actions={
          <>
            <Tooltip title={t('generateText.history')} mouseEnterDelay={0.3}>
              <button
                type="button"
                className="pcv-media-hover-btn"
                aria-label={t('generateText.history')}
                onClick={() => setHistoryOpen((v) => !v)}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
              <button
                type="button"
                className="pcv-media-hover-btn"
                aria-label={mediaHeader.duplicate}
                onClick={handleDuplicateNode}
                data-testid="generate-text-duplicate-btn"
              >
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={hasResult ? t('generateText.download') : t('generateText.downloadDisabled')} mouseEnterDelay={0.3}>
              <button
                type="button"
                className="pcv-media-hover-btn"
                aria-label={t('generateText.download')}
                disabled={!hasResult}
                onClick={handleDownloadResult}
              >
                <DownloadOutlined />
              </button>
            </Tooltip>
            <Tooltip title={t('generateText.deleteNode')} mouseEnterDelay={0.3}>
              <button
                type="button"
                className="pcv-media-hover-btn pcv-media-hover-btn--danger"
                aria-label={t('generateText.deleteNode')}
                onClick={handleDelete}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {historyOpen ? (
        <div className="pcv-script-history-panel nodrag" onClick={(e) => e.stopPropagation()}>
          <HistoryList entries={generationHistory} />
        </div>
      ) : null}

      {/* ② Result */}
      <div
        className={`pcv-gen-result-area pcv-gen-text-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
        onClick={() => nodeData.onExpandFunctionCardDetail?.(id)}
      >
        <div className="pcv-gen-result-section-label">
          <span>{t('generateText.resultSection')}</span>
          <span
            className={`pcv-gen-result-status-badge${hasResult && !isBusy ? ' is-ready' : ''}${isBusy ? ' is-busy' : ''}${runStatus === 'ERROR' ? ' is-error' : ''}`}
          >
            {resultStatusBadge}
          </span>
        </div>

        {!hasResult && !isBusy && runStatus !== 'ERROR' ? (
          <div className="pcv-gen-result-empty">
            <span className="pcv-gen-result-empty-icon">{def.icon}</span>
            <span>{t('generateText.resultEmptyTitle')}</span>
            <span className="pcv-gen-result-empty-hint">
              {showExpanded ? t('generateText.resultEmptyHintExpanded') : t('generateText.resultEmptyHintCollapsed')}
            </span>
          </div>
        ) : null}

        {isBusy ? (
          <div className="pcv-gen-result-loading">
            <LoadingOutlined spin />
            <span>{t('generateText.statusGeneratingEllipsis')}</span>
          </div>
        ) : null}

        {!isBusy && hasResult && presentedResult ? (
          <>
            {needsRegeneration ? (
              <div className="pcv-candidate-stale-banner">{t('generateText.staleBanner')}</div>
            ) : null}
            <TextResultBody result={presentedResult} showExpanded={showExpanded} />
          </>
        ) : null}

        {shouldShowGenerateTextTenantFailureBanner(runStatus, isBusy) ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">
            {t(resolveGenerateTextTenantFailureI18nKey(fields.errorCode as string | null))}
          </div>
        ) : null}
      </div>

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()}>
          {isPromptEnhancement ? (
            <div
              className="pcv-gen-billing-hint"
              data-testid="video-prompt-enhancement-billing-hint"
              style={{ marginBottom: 8 }}
            >
              {t('generation.promptEnhanceBillingHint')}
            </div>
          ) : null}
          {isPromptEnhancement ? (
            <div
              className="pcv-brief-section pcv-video-input-section"
              data-testid="video-prompt-enhancement-bound-input"
              ref={promptContextSectionRef}
            >
              {showExpanded ? (
                <VideoInputSectionHandle
                  id={VIDEO_PROMPT_CONTEXT_IN_HANDLE}
                  className="pcv-handle pcv-handle--prompt-context"
                  testId="generate-text-prompt-context-in-handle"
                  dropZoneHandle={false}
                />
              ) : null}
              <span className="pcv-brief-section-label">
                {t('generation.promptEnhanceBoundInputLabel')}
              </span>
              <p className="pcv-gen-text-section-hint">
                {t('generation.promptEnhanceBoundInputSource')}
              </p>
              <div
                className="pcv-video-format-locked"
                style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}
              >
                {boundOriginalPrompt || t('generation.promptEnhanceBoundInputEmpty')}
              </div>
            </div>
          ) : null}
          {/* ③ Input assets */}
          <div className="pcv-brief-section pcv-gen-text-assets-section pcv-gen-related-assets-drop-zone">
            {inputHandleVisibility.showExpandedSectionAggregateHandle ? (
              <div
                className="pcv-gen-related-assets-slot-handle-stack"
                data-testid="generate-text-assets-slot-handle-stack"
              >
                <VideoInputSectionHandle
                  id={GENERIC_INPUT_HANDLE}
                  className="pcv-handle pcv-handle--structured-asset pcv-dropzone-handle"
                  testId="generate-text-expanded-aggregate-in-handle"
                />
                {stackedPerEdgeTargetHandleIds.map((handleId) => (
                  <Handle
                    key={`slot-stack::${handleId}`}
                    type="target"
                    position={Position.Left}
                    id={handleId}
                    className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden pcv-handle--slot-stacked"
                  />
                ))}
              </div>
            ) : null}
            <div className="pcv-gen-text-section-header-row">
              <span className="pcv-brief-section-label">
                {t('generateText.inputAssets')}
                {referenceImageCount > 0
                  ? ` · ${t('generateText.referenceImagesCount', {
                      current: referenceImageCount,
                      max: GENERATE_TEXT_MAX_REFERENCE_IMAGES,
                    })}`
                  : assetCount > 0
                    ? `（${assetCount}）`
                    : ''}
              </span>
              {copyableResultText ? (
                <button
                  type="button"
                  className="pcv-gen-text-copy-result-btn nodrag"
                  data-testid="generate-text-copy-result-btn"
                  disabled={!copyableResultText}
                  onClick={handleCopyResultSection}
                >
                  {sectionCopyFeedback ? t('generateText.copied') : t('generateText.copyText')}
                </button>
              ) : null}
            </div>
            <p className="pcv-gen-text-section-hint">{t('generateText.inputAssetsHint')}</p>
            {contextMaterials.length > 0 ? (
              <span className="pcv-input-materials-hint">{t('inputMaterials.dragHint')}</span>
            ) : null}
            <div className="pcv-gen-text-asset-list">
              {contextMaterials.length > 0 ? (
                <InputMaterialsRow
                  workspaceId={workspaceId}
                  materials={contextMaterials}
                  hideLabel
                  draggableMentions
                  onDisconnect={(edgeId) => {
                    const nextInstruction = stripMentionsForEdge(additionalInstruction, edgeId)
                    if (nextInstruction !== additionalInstruction) {
                      markParamsDirty({ additionalInstruction: nextInstruction })
                    }
                    nodeData.onDisconnectMaterial?.(id, edgeId)
                  }}
                  onSelectSource={(nodeId) => nodeData.onSelectNode?.(nodeId)}
                  onEditText={(nodeId) => nodeData.onRequestEditText?.(nodeId)}
                />
              ) : null}
              {!contextMaterials.length && Array.isArray(fields.inputAssets) && fields.inputAssets.length > 0
                ? (fields.inputAssets as Array<{
                    assetId: string
                    assetType?: string | null
                    displayName?: string | null
                    generatedOutputId?: string | null
                    sourceNodeId?: string | null
                    context?: string | null
                  }>)
                    .filter((a) => (a.assetType || 'image') !== 'text')
                    .map((a) => (
                    <div key={a.assetId} className="pcv-gen-text-asset-card nodrag">
                      <div className="pcv-gen-text-asset-thumb">
                        <FieldAssetThumb
                          workspaceId={workspaceId}
                          entityId={a.assetId}
                          generatedOutputId={a.generatedOutputId}
                        />
                      </div>
                      <div className="pcv-gen-text-asset-meta">
                        <div className="pcv-gen-text-asset-source">
                          {t('generateText.sourceFrom', {
                            label: a.displayName || t('generateText.inputAssetFallback'),
                          })}
                        </div>
                        <div className="pcv-gen-text-asset-count">{t('generateText.selectedImages', { count: 1 })}</div>
                      </div>
                      <button type="button" className="pcv-gen-text-asset-replace nodrag" onClick={openReplaceAssetPicker}>
                        <SwapOutlined /> {t('generateText.replaceAsset')}
                      </button>
                    </div>
                  ))
                : null}
              {!contextMaterials.length &&
              !(
                Array.isArray(fields.inputAssets) &&
                fields.inputAssets.some((raw) => {
                  if (!raw || typeof raw !== 'object') return false
                  const row = raw as { assetType?: string | null; assetId?: string | null; generatedOutputId?: string | null }
                  return Boolean(row.assetId || row.generatedOutputId)
                })
              ) ? (
                <div className="pcv-script-context-compact is-empty">{t('generateText.inputAssetsEmpty')}</div>
              ) : null}
            </div>
            {!assetCount ? (
              <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={openReplaceAssetPicker}>
                <PlusOutlined /> {t('generateText.addAsset')}
              </button>
            ) : null}
          </div>

          {/* ④ Params */}
          <div className="pcv-gen-params-header">
            <span className="pcv-brief-section-label">{t('generateText.parametersSection')}</span>
          </div>
          <div className="pcv-gen-params-primary pcv-script-params-row">
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('generateText.paramLanguage')}</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={language}
                options={languageOptions}
                onChange={(v) => markParamsDirty({ language: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('generateText.paramLength')}</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={contentLength}
                options={lengthOptions}
                onChange={(v) => markParamsDirty({ contentLength: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('generateText.paramFormat')}</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={outputFormat}
                options={formatOptions}
                onChange={(v) => markParamsDirty({ outputFormat: v })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
          </div>

          {/* ⑤ 补充要求 */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('generateText.additionalInstruction')}{' '}
              <span className="pcv-brief-section-optional">（{t('generateText.optional')}）</span>
            </span>
            <p className="pcv-gen-text-section-hint">{t('generateText.instructionHint')}</p>
            <div data-testid="generate-text-additional-instruction-editor">
              <AssetMentionPromptEditor
                value={additionalInstruction}
                onChange={handleAdditionalInstructionChange}
                aliasByEdgeId={aliasByEdgeId}
                thumbUrlByEdgeId={thumbUrlByEdgeId}
                placeholder={t('generateText.instructionPlaceholder')}
              />
            </div>
            <span className="pcv-brief-textarea-counter">
              {additionalInstruction.length}/{GENERATE_TEXT_INSTRUCTION_MAX}
            </span>
            {disconnectedMentionCount > 0 ? (
              <div className="pcv-gen-capability-warning">
                {t('generation.disconnectedMentionsCount', { count: disconnectedMentionCount })}
              </div>
            ) : null}
            {suggestedChips.length ? (
              <div className="pcv-gen-text-quick-row nodrag">
                <span className="pcv-gen-text-quick-label">{t('generateText.quickInput')}</span>
                <div className="pcv-brief-chip-row">
                  {suggestedChips.map((chip) => (
                    <span
                      key={`${chipBatch}-${chip}`}
                      className="pcv-brief-chip"
                      onClick={() => {
                        const next = additionalInstruction ? `${additionalInstruction.trim()} ${chip}`.trim() : chip
                        markParamsDirty({ additionalInstruction: next.slice(0, GENERATE_TEXT_INSTRUCTION_MAX) })
                      }}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                {suggestedChipsAll.length > CHIP_PAGE ? (
                  <button
                    type="button"
                    className="pcv-gen-text-chip-shuffle"
                    onClick={() => setChipBatch((n) => n + 1)}
                  >
                    {t('generateText.shuffleChips')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* ⑥ Footer — Provider | Skill | Generate (no Diverge in V1) */}
          <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={providerSelectView.tooltipTitle} mouseEnterDelay={0.4}>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={resolvedProviderOptionId ?? undefined}
                disabled={providerSelectView.selectDisabled}
                placeholder={providerSelectView.placeholder}
                options={providerSelectView.options}
                optionLabelProp="label"
                onChange={(v) => {
                  const fromCatalog = findProviderOptionById(providerOptions, v)
                  const fromView = providerSelectView.options.find((o) => o.value === v)
                  const displayName = fromCatalog?.frontend_display_name || fromView?.label
                  if (workspaceId && displayName) {
                    writeProviderDisplayNameToCache(workspaceId, v, displayName)
                  }
                  patchFields(buildExplicitProviderSelectionPatch(v, displayName))
                }}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </Tooltip>
            <Tooltip
              title={
                resolvedSkill
                  ? `${resolvedSkill.display_name}${resolvedSkill.skill_version ? ` · ${resolvedSkill.skill_version}` : ''}`
                  : t('generateText.skillUnavailableReselectShort')
              }
              mouseEnterDelay={0.4}
            >
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={skillSelectValue}
                options={skillSelectOptions as any}
                optionLabelProp="label"
                onChange={(v) =>
                  patchFields(
                    v === 'AUTO'
                      ? { skillSelectionSource: 'AUTO', skillKey: null }
                      : {
                          skillSelectionSource: 'EXPLICIT',
                          skillKey: v,
                          skillVersion:
                            skills.find((s) => s.skill_key === v)?.skill_version ||
                            resolvedSkill?.skill_version ||
                            'v1',
                        },
                  )
                }
                popupMatchSelectWidth={false}
                listHeight={360}
                virtual={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
                data-testid="generate-text-skill-select"
              />
            </Tooltip>
            <Tooltip title={!canGenerate && blockingHint ? blockingHint : undefined} mouseEnterDelay={0.2}>
              <button
                type="button"
                className="pcv-gen-footer-generate-btn"
                data-testid="generate-text-btn"
                disabled={!canGenerate}
                aria-disabled={!canGenerate}
                aria-describedby={!canGenerate && blockingHint ? `generate-text-hint-${id}` : undefined}
                onClick={() => {
                  void handleGenerate()
                }}
              >
                {isBusy ? <LoadingOutlined spin /> : null}{' '}
                {isBusy
                  ? t('generateText.runGenerating')
                  : hasResult
                    ? t('generateText.runRegenerate')
                    : t('generateText.runGenerate')}
              </button>
            </Tooltip>
          </div>
          {!canGenerate && blockingHint && !isBusy ? (
            <div className="pcv-gen-footer-hint" id={`generate-text-hint-${id}`} data-testid="generate-text-readiness-hint">
              {blockingHint}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function FieldAssetThumb({
  workspaceId,
  entityId,
  generatedOutputId,
  nodeType,
}: {
  workspaceId: string
  entityId?: string | null
  generatedOutputId?: string | null
  nodeType?: string | null
}) {
  const thumb = useImageAssetThumb(workspaceId, { entityId, generatedOutputId, nodeType })
  return thumb ? (
    <img src={thumb} alt="" draggable={false} />
  ) : (
    <div className="pcv-gen-ref-chip-empty">
      <PictureOutlined />
    </div>
  )
}

export default memo(GenerateTextNodeImpl)
